import { z } from 'zod';
/**
 * Occasions at the business's location (spec 110 section 4.8, S160-3,
 * REQ-AUT-006..008): outside events worth a post. The daily routine detects
 * them per property and asks the `topic_planner` for one idea that names the
 * occasion; the idea's `topic_proposal` message carries the occasion.
 */
export const OCCASION_KINDS = ['own_date', 'public_holiday', 'school_holidays', 'fresh_snow', 'first_warm_day', 'heat_day', 'sunny_weekend'];
export const WEATHER_OCCASION_KINDS = ['fresh_snow', 'first_warm_day', 'heat_day', 'sunny_weekend'];
export const OCCASION_COUNTRIES = ['AT', 'DE'];
/** ISO 3166-2 subdivision of a supported country, e.g. `AT-6` (Styria) or `DE-BY` (Bavaria). */
export const OCCASION_SUBDIVISION_PATTERN = /^(AT|DE)-[A-Z0-9]{1,3}$/;
export const OCCASION_LIMITS = {
    placeNameMaxLength: 200,
    titleMaxLength: 120,
    noteMaxLength: 500,
    labelMaxLength: 120,
    weeklyLimit: { min: 1, max: 7 },
    /** Own dates may lie at most this many days ahead. */
    ownDateMaxDaysAhead: 365,
    maxUpcomingOwnDates: 200,
    placeQuery: { min: 2, max: 120 },
    maxPlaces: 5,
    /** Days the detection looks ahead (preview and routine). */
    weatherDays: 5,
    holidayFromDays: 3,
    holidayToDays: 14,
    ownFromDays: 2,
    ownToDays: 21,
};
export const OCCASION_SETTINGS_DEFAULTS = {
    enabled: false,
    weather_enabled: true,
    holidays_enabled: true,
    weekly_limit: 1,
};
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** The occasion an idea was planned for: provenance on the `topic_planner` params and in the `topic_proposal` data. */
export const occasionRefSchema = z
    .object({
    kind: z.enum(OCCASION_KINDS),
    date: isoDate,
    /** The holiday's or own date's name; empty for weather occasions. */
    label: z.string().max(OCCASION_LIMITS.labelMaxLength),
})
    .strict();
/**
 * Occasion posts on standby (spec 110 section 4.9, E300 S300-1,
 * REQ-KAMP-004..010): a content item bound to a trigger waits without a date
 * and is fired by the detection (the occasion kinds) or by a person (`manual`).
 */
export const OCCASION_TRIGGER_KINDS = [...OCCASION_KINDS, 'manual'];
/** Trigger kinds that may narrow the holiday by its name (`label_match`). */
export const OCCASION_LABEL_MATCH_KINDS = ['public_holiday', 'school_holidays'];
export const OCCASION_STANDBY_STATUSES = ['standby', 'fired', 'dated', 'cancelled'];
/** What module 50's guard reports for a content item (spec 110 section 4.9 rule 5). */
export const OCCASION_GUARD_STATES = ['none', 'standby', 'finalising', 'fired', 'dated'];
/** Who fired a standby: the daily detection (a forecast or a calendar date) or a person. */
export const OCCASION_FIRE_SOURCES = ['detection', 'person'];
export const OCCASION_STANDBY_LIMITS = {
    confirmationMaxLength: 300,
    /** `content_item_ids` of one list request. */
    maxContentItemIds: 100,
};
/** The `origin` of the finalisation chain's runs (spec 80 section 4.19): module 110 recognises its reviewer run by it. */
export const OCCASION_FINALIZE_ORIGIN = 'occasion_finalize';
//# sourceMappingURL=occasions.js.map